import { useEffect, useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';

// ── Your product ID ──
export const REMOVE_ADS_PRODUCT_ID = 'remove_ads';

// ── Your RevenueCat API key ──
const REVENUECAT_API_KEY = 'goog_tIMIvflHKMQScawcideHUTroaYE';

// ── localStorage key ──
const REMOVE_ADS_KEY = 'calc360_remove_ads';

// ✅ PWA-Safe Check
const isCapacitor = () => Capacitor.isNativePlatform();

const lsGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const lsSet = (key, val) => { try { localStorage.setItem(key, val); } catch { /* no-op */ } };

export const useInAppPurchase = ({ onAdsRemoved }) => {
  const [adsRemoved, setAdsRemoved]   = useState(() => lsGet(REMOVE_ADS_KEY) === 'true');
  const [price, setPrice]             = useState('₹99');
  const [purchasePackage, setPurchasePackage] = useState(null); 
  const [purchasing, setPurchasing]   = useState(false);
  const [restoring, setRestoring]     = useState(false);
  const [error, setError]             = useState(null);

  const rcReady = useRef(false);

  // ── Configure RC once, verify status, and fetch price/package ──
  useEffect(() => {
    // ✅ Early exit for Web/PWA so RevenueCat doesn't crash
    if (!isCapacitor()) return;

    const init = async () => {
      try {
        const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');

        if (!rcReady.current) {
          await Purchases.setLogLevel({ level: LOG_LEVEL.ERROR });
          await Purchases.configure({ apiKey: REVENUECAT_API_KEY });
          rcReady.current = true;
        }

        const { customerInfo } = await Purchases.getCustomerInfo();
        if (customerInfo.entitlements.active['remove_ads']) {
            if (!adsRemoved) markAdsRemoved();
            return; 
        }

        const offerings = await Purchases.getOfferings();
        
        if (offerings?.current?.availablePackages?.length > 0) {
          const pkg = offerings.current.availablePackages[0];
          setPrice(pkg.product.priceString);
          setPurchasePackage(pkg); 
        }
      } catch (err) {
        console.warn('[IAP] init failed:', err);
        rcReady.current = false;
      }
    };

    init();
  }, [adsRemoved]);

  const markAdsRemoved = useCallback(() => {
    lsSet(REMOVE_ADS_KEY, 'true');
    setAdsRemoved(true);
    onAdsRemoved?.();
  }, [onAdsRemoved]);

  // ── Purchase remove ads ──
  const purchaseRemoveAds = useCallback(async () => {
    if (!isCapacitor()) return; // ✅ Web Fallback
    setPurchasing(true);
    setError(null);

    try {
      const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');

      if (!rcReady.current) {
        await Purchases.setLogLevel({ level: LOG_LEVEL.ERROR });
        await Purchases.configure({ apiKey: REVENUECAT_API_KEY });
        rcReady.current = true;
      }

      let pkgToBuy = purchasePackage;

      if (!pkgToBuy) {
        const offerings = await Purchases.getOfferings();
        
        if (!offerings?.current?.availablePackages?.length) {
          throw new Error('No packages found in RevenueCat dashboard.');
        }
        
        pkgToBuy = offerings.current.availablePackages[0];
      }

      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkgToBuy });

      if (customerInfo.entitlements.active['remove_ads']) {
        markAdsRemoved();
      }
    } catch (err) {
      const code = err?.code;
      const isCancelled =
        code === 1 ||
        code === 'PURCHASE_CANCELLED' ||
        String(err?.message ?? '').toLowerCase().includes('cancel');

      if (!isCancelled) {
        console.error('[IAP] FINAL CRASH REASON:', err?.message || 'Unknown error');
        setError(`Failed: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setPurchasing(false);
    }
  }, [markAdsRemoved, purchasePackage]);

  // ── Restore purchases ──
  const restorePurchases = useCallback(async () => {
    if (!isCapacitor()) return; // ✅ Web Fallback
    setRestoring(true);
    setError(null);

    try {
      const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');

      if (!rcReady.current) {
        await Purchases.setLogLevel({ level: LOG_LEVEL.ERROR });
        await Purchases.configure({ apiKey: REVENUECAT_API_KEY });
        rcReady.current = true;
      }

      const { customerInfo } = await Purchases.restorePurchases();

      if (customerInfo.entitlements.active['remove_ads']) {
        markAdsRemoved();
      } else {
        setError('No previous purchase found.');
      }
    } catch (err) {
      setError(`Restore failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setRestoring(false);
    }
  }, [markAdsRemoved]);

  return {
    adsRemoved,
    price,
    purchasing,
    restoring,
    error,
    purchaseRemoveAds,
    restorePurchases,
  };
};